import { isEmptyString } from '@/utils/string';
import { isUnitedStates } from './address-options';
import type { CreditCardFormAddress } from './credit-card-form-submit';

/**
 * The rules every tax-rate lookup obeys, whichever form asks for it. Checkout and credit purchase both
 * collect a billing address and both price the total with the rate it returns, so the answers to "is this
 * address worth a request", "how long do we wait for the typing to stop", and "which fields are typed"
 * have to be the same on both. They are stated once here.
 *
 * What stays with each form is only what really differs: which address fields that form's vendor keys
 * the request on, and how it decides a change was typed rather than selected.
 */

/**
 * A zip must reach this length before we ask the tax service for a rate; anything shorter cannot be
 * geocoded.
 */
export const MIN_ZIP_LENGTH = 3;

/** How long a typed address change waits before its lookup is sent. */
export const TAX_LOOKUP_DEBOUNCE_MS = 300;

/**
 * The fields the user types a character at a time, where a lookup per keystroke would be wasteful.
 * Country and state come from selects, so one change there is one deliberate choice and re-prices at
 * once.
 */
export const TAX_DEBOUNCED_FIELDS = [
  'zipCode',
  'addressLine1',
] as const satisfies readonly (keyof CreditCardFormAddress)[];

/** Whether a change to this field waits out {@link TAX_LOOKUP_DEBOUNCE_MS} before its lookup is sent. */
export function isDebouncedTaxField(field: keyof CreditCardFormAddress): boolean {
  return TAX_DEBOUNCED_FIELDS.some((debounced) => debounced === field);
}

/**
 * Whether the address is complete enough to ask for a tax rate. We ask only for US addresses, and a US
 * address needs a state and a geocodable zip, because both vendors key the lookup on the region.
 *
 * This is a statement about the data we collect, not a claim that only the US is taxable. Skipping the
 * lookup elsewhere also does not misstate the price, because the charge paths are gated the same way:
 *
 * - Legacy Stripe/TaxJar is gated on `IsTaxable(country, state)`, hardcoded to the US and to eight
 *   states, on the lookup (`ui_api/billing/tax_rate.go:52`) and on the charge (`billing/tax.go:133`).
 * - Ordway/Avalara is gated on its registered nexuses instead: an address whose region matches no state
 *   nexus returns before Avalara is ever called (`billing/tax.go:271`), and the charge path resolves
 *   through the same gate (`tax.go:169`). That nexus list is account configuration rather than code, so a
 *   non-US address is not provably untaxable there — we just never collect a region outside the US to
 *   send, because the state select only renders for the US
 *   (`credit-card/billing-address-fields.tsx`).
 *
 * Hence the explicit country check. Gating on a non-empty state alone is equivalent only for as long as
 * that hidden field stays blank: a saved non-US address arriving with a region pre-filled would start
 * firing lookups that can only come back empty.
 */
export function hasTaxableAddress(address: CreditCardFormAddress): boolean {
  return isUnitedStates(address.country) && !isEmptyString(address.state) && address.zipCode.length >= MIN_ZIP_LENGTH;
}
